import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Box,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  People as PeopleIcon,
  Groups as TeamsIcon,
  EmojiEvents as LeaguesIcon,
  SportsSoccer as GamesIcon,
  CheckCircle as CompletedIcon,
  Schedule as UpcomingIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import OrganizationSettings from '../../../components/admin/organization/OrganizationSettings';
import unifiedApi from '../../../utils/unifiedApi';
import { useCanViewOutstandingBalances, useIsAdmin, useIsSuperAdmin } from '../../../contexts/PermissionsContext';
import { Merge as MergeIcon, Email as EmailIcon, Autorenew as RecalcIcon, NotificationsActive as ReminderIcon, AccountBalanceWallet as WalletIcon, Forum as ChatIcon } from '@mui/icons-material';

const OrganizationAdminDashboard = () => {
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const canViewOutstandingBalances = useCanViewOutstandingBalances();
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    tenantId: 0,
    tenantName: '',
    totalUsers: 0,
    totalLeagues: 0,
    totalTeams: 0,
    totalGames: 0,
    completedGames: 0,
    upcomingGames: 0,
  });
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [error, setError] = useState(null);

  // Draft stats recalculation (Super Admin)
  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcFeedback, setRecalcFeedback] = useState(null);

  const handleRecalculateDraftStats = useCallback(async () => {
    setRecalcConfirmOpen(false);
    setRecalcLoading(true);
    try {
      await unifiedApi.recalculateDraftStats();
      setRecalcFeedback({ severity: 'success', message: 'Draft statistics recalculated successfully.' });
    } catch (err) {
      setRecalcFeedback({ severity: 'error', message: err.message || 'Failed to recalculate draft statistics.' });
    } finally {
      setRecalcLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await unifiedApi.getTenantStats();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeagues = useCallback(async () => {
    setLeaguesLoading(true);
    try {
      const basicLeagues = await unifiedApi.getLeagues();
      
      // Fetch stats for each league to get team and member counts
      const leaguesWithStats = await Promise.all(
        basicLeagues.map(async (league) => {
          try {
            const stats = await unifiedApi.getLeagueStats(league.id);
            return {
              ...league,
              teamCount: stats.totalTeams,
              memberCount: stats.totalUsers,
              isActive: stats.isActive
            };
          } catch (err) {
            console.error(`Failed to fetch stats for league ${league.id}`, err);
            return {
              ...league,
              teamCount: 0,
              memberCount: 0
            };
          }
        })
      );
      
      setLeagues(leaguesWithStats || []);
    } catch (err) {
      console.error('Failed to fetch leagues', err);
    } finally {
      setLeaguesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchLeagues();
  }, [fetchStats, fetchLeagues]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  const sortedLeagues = [...leagues].sort((a, b) => {
    const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
    const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
    return bDate - aDate;
  });

  const statCards = [
    {
      title: 'Leagues',
      value: stats.totalLeagues,
      icon: <LeaguesIcon sx={{ fontSize: 40, color: '#f57c00' }} />,
      color: '#fff3e0',
    },
    {
      title: 'Teams',
      value: stats.totalTeams,
      icon: <TeamsIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      color: '#e8f5e8',
    },

    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: <PeopleIcon sx={{ fontSize: 40, color: '#2E4156' }} />,
      color: '#e3f2fd',
      link: `/admin/members`,
    },

    {
      title: 'Total Games',
      value: stats.totalGames,
      icon: <GamesIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
      color: '#f3e5f5',
    },
    {
      title: 'Completed Games',
      value: stats.completedGames,
      icon: <CompletedIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      color: '#e8f5e8',
    },
    {
      title: 'Upcoming Games',
      value: stats.upcomingGames,
      icon: <UpcomingIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
      color: '#fff4e6',
    },
  ];

  return (
    <AdminLayout>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
        >
          <Tab label='Dashboard' />
          <Tab label='Settings' icon={<SettingsIcon />} iconPosition='start' />
        </Tabs>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tab Content */}
      {tabValue === 0 && (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Statistics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {statCards.map((card, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
                    <Card sx={{ height: '100%', bgcolor: card.color }}>
                      <CardContent>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          {card.icon}
                          <Box sx={{ ml: 2 }}>
                            <Typography variant='h4' component='div'>
                              {card.value}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {card.title}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                      {card.link && (
                        <CardActions>
                          <Button size='small' component={Link} to={card.link}>
                            View
                          </Button>
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Leagues Management Section */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant='h5'>
                    Leagues
                  </Typography>
                  {isAdmin && (
                  <Button 
                    variant='contained' 
                    component={Link}
                    to={`/admin/leagues/create`}
                    startIcon={<AddIcon />}
                  >
                    Create League
                  </Button>
                  )}
                </Box>

                {leaguesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>League Name</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell align="center">Teams</TableCell>
                            <TableCell align="center">Members</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedLeagues.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No leagues found. Create your first league to get started.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedLeagues.map((league) => (
                              <TableRow key={league.id} hover>
                                <TableCell>
                                  <Typography 
                                    variant="body1" 
                                    fontWeight="medium"
                                    component={Link}
                                    to={`/admin/leagues/${league.id}`}
                                    sx={{ 
                                      textDecoration: 'none',
                                      color: 'primary.main',
                                      '&:hover': {
                                        textDecoration: 'underline'
                                      }
                                    }}
                                  >
                                    {league.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDate(league.startDate)}</TableCell>
                                <TableCell>{formatDate(league.endDate)}</TableCell>
                                <TableCell align="center">{league.teamCount}</TableCell>
                                <TableCell align="center">{league.memberCount}</TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={league.isActive ? 'Active' : 'Archived'}
                                    color={league.isActive ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                    <Tooltip title="League Admin Dashboard">
                                      <Button
                                        size="small"
                                        variant="contained"
                                        component={Link}
                                        to={`/admin/leagues/${league.id}`}
                                      >
                                        Admin
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="View League Members">
                                      <IconButton
                                        size="small"
                                        component={Link}
                                        to={`/admin/leagues/${league.id}/members`}
                                      >
                                        <PeopleIcon />
                                      </IconButton>
                                    </Tooltip>
                                    {isAdmin && (
                                    <Tooltip title="League Settings">
                                      <IconButton
                                        size="small"
                                        component={Link}
                                        to={`/admin/leagues/${league.id}/settings`}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>

                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      {sortedLeagues.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center">
                          No leagues found. Create your first league to get started.
                        </Typography>
                      ) : (
                        sortedLeagues.map((league) => (
                          <Paper 
                            key={league.id} 
                            elevation={1} 
                            sx={{ 
                              p: 2, 
                              mb: 2, 
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <Typography 
                              variant="h6" 
                              component={Link}
                              to={`/admin/leagues/${league.id}`}
                              sx={{ 
                                textDecoration: 'none',
                                color: 'primary.main',
                                display: 'block',
                                mb: 2,
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {league.name}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">Season:</Typography>
                                <Typography variant="body2" fontWeight="medium">{formatDate(league.startDate)} – {formatDate(league.endDate)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">Teams:</Typography>
                                <Typography variant="body2" fontWeight="medium">{league.teamCount}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">Members:</Typography>
                                <Typography variant="body2" fontWeight="medium">{league.memberCount}</Typography>
                              </Box>
                              <Chip 
                                label={league.isActive ? 'Active' : 'Archived'}
                                color={league.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                component={Link}
                                to={`/admin/leagues/${league.id}`}
                              >
                                Admin
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/admin/leagues/${league.id}/members`}
                                startIcon={<PeopleIcon />}
                              >
                                Members
                              </Button>
                              {isAdmin && (
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/admin/leagues/${league.id}/settings`}
                                startIcon={<EditIcon />}
                              >
                                Settings
                              </Button>
                              )}
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Box>
                  </>
                )}
              </Paper>

              {/* Quick Actions */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant='h6' gutterBottom>
                      Quick Actions
                    </Typography>
                    <Stack spacing={2}>
                      <Button
                        variant='outlined'
                        component={Link}
                        to={`/admin/members`}
                        startIcon={<PeopleIcon />}
                      >
                        Manage All Members
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          variant='outlined'
                          component={Link}
                          to='/admin/merge-users'
                          startIcon={<MergeIcon />}
                        >
                          Merge Users
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button
                          variant='outlined'
                          onClick={() => setRecalcConfirmOpen(true)}
                          disabled={recalcLoading}
                          startIcon={recalcLoading ? <CircularProgress size={20} /> : <RecalcIcon />}
                        >
                          Recalculate Draft Stats
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button
                          variant='outlined'
                          component={Link}
                          to='/admin/game-reminders'
                          startIcon={<ReminderIcon />}
                        >
                          Send Game Reminder
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant='outlined'
                          component={Link}
                          to='/admin/chat'
                          startIcon={<ChatIcon />}
                        >
                          Chat Admin
                        </Button>
                      )}
                      {canViewOutstandingBalances && (
                        <Button
                          variant='outlined'
                          component={Link}
                          to='/admin/outstanding-balances'
                          startIcon={<WalletIcon />}
                        >
                          Outstanding Balances
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant='outlined'
                          component={Link}
                          to='/admin/compose-email'
                          startIcon={<EmailIcon />}
                        >
                          Compose Email
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant='h6' gutterBottom>
                      Recent Activity
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Activity tracking will be implemented here.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      {/* Settings Tab */}
      {tabValue === 1 && <OrganizationSettings canEdit={isAdmin} />}

      {/* Recalculate Draft Stats confirmation */}
      <Dialog open={recalcConfirmOpen} onClose={() => setRecalcConfirmOpen(false)}>
        <DialogTitle>Recalculate Draft Statistics</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This rebuilds the precomputed player draft statistics (games played, goals, cards,
            injuries, etc.) for this organization now, instead of waiting for the nightly 3am job.
            It may take a moment to complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalcConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRecalculateDraftStats} variant='contained'>
            Recalculate Now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!recalcFeedback}
        autoHideDuration={6000}
        onClose={() => setRecalcFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {recalcFeedback ? (
          <Alert
            onClose={() => setRecalcFeedback(null)}
            severity={recalcFeedback.severity}
            sx={{ width: '100%' }}
          >
            {recalcFeedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AdminLayout>
  );
};

export default OrganizationAdminDashboard;
